import React from 'react'
import { Box, Typography } from '@mui/material'
import { ClockIcon } from '@heroicons/react/24/outline'
import type { FacilitatorLiveSessionCard } from '../../../../../../pure-js/types/FacilitatorLiveSessionMonitor'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { Modal } from '../../../../shared/components/Modal'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import {
  formatLoopSummaryPresence,
  formatLoopSummaryStage,
  formatLoopTimes,
  getLoopStatusColors,
  getLoopStatusLabel
} from './controlTowerHelpers'

interface ControlTowerLoopDetailsModalProps {
  card: FacilitatorLiveSessionCard | null
  onClose: () => void
}

/** Every loop of a game card, newest first, with its phase, times and who actively played it. */
export const ControlTowerLoopDetailsModal: React.FC<ControlTowerLoopDetailsModalProps> = ({ card, onClose }) => {
  const { t } = useTranslation()
  const loops = (card?.loopSummaries || []).slice().sort((left, right) => right.displayIndex - left.displayIndex)
  const title = card
    ? t('facilitator.controlTower.loopDetailsTitle', '{{team}} loop details', { team: card.teamName })
    : t('facilitator.controlTower.loopDetails', 'Loop details')

  return (
    <Modal isOpen={Boolean(card)} onClose={onClose} title={title} maxWidth="md">
      <Box sx={{ display: 'grid', gap: 1.25 }}>
        {card ? (
          <Typography sx={{ color: INTERAX_COLORS.white70, fontSize: '14px' }}>{card.activityTitle}</Typography>
        ) : null}
        {loops.map((loop) => {
          const loopStatusColors = getLoopStatusColors(loop)
          const loopTimes = formatLoopTimes(loop)

          return (
            <Box
              key={loop.loopId}
              data-testid={`control-tower-loop-summary-${loop.loopId}`}
              sx={{
                display: 'grid',
                gap: 1,
                borderRadius: '16px',
                p: 1.5,
                backgroundColor: INTERAX_COLORS.white04,
                border: `1px solid ${INTERAX_COLORS.white10}`
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={{ color: INTERAX_COLORS.white, fontWeight: 600 }}>{loop.loopLabel}</Typography>
                <Box sx={{ px: 1, py: 0.5, borderRadius: '999px', backgroundColor: loopStatusColors.background }}>
                  <Typography sx={{ color: loopStatusColors.color, fontSize: '12px', fontWeight: 700 }}>
                    {getLoopStatusLabel(loop, t)}
                  </Typography>
                </Box>
              </Box>

              <Typography sx={{ color: INTERAX_COLORS.white70, fontSize: '14px' }}>
                {formatLoopSummaryStage(loop, t)}
              </Typography>

              {loopTimes ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: INTERAX_COLORS.white70 }}>
                  <ClockIcon style={{ width: 14, height: 14 }} />
                  <Typography sx={{ color: INTERAX_COLORS.white70, fontSize: '13px' }}>{loopTimes}</Typography>
                </Box>
              ) : null}

              <Typography sx={{ color: INTERAX_COLORS.white70, fontSize: '13px' }}>
                {formatLoopSummaryPresence(loop, t)}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Modal>
  )
}
